// src/client/renderer/vfx/items/mystery-box.ts
//
// Plan 17, Task 4.2 — Mystery Box pool (portal & outcome visuals).
//
// Audio:
//   Manager fires the item cue only for phase='cast'. Outcome, teleport, and
//   trapped visuals are intentionally silent follow-up emitters.
//
// Triggered by FOUR events:
//   1. ItemCastEvent with itemId='mystery_box' → phase='cast':
//        Question-mark glyph spinning at caster (700ms anticipation).
//
//   2. MysteryBoxOutcomeEvent → phase='impact':
//        Per-outcome sprite at caster:
//          'blue'             → colour 0x66ccff (blue-portal sprite)
//          'gold'             → colour 0xffd24a (gold-trophy sprite)
//          'dark'             → colour 0x440044 (dark-skull sprite)
//          'gold_fallback_item' → colour 0xffaa66 (gold-question sprite)
//
//   3. MysteryBoxTeleportEvent → phase='active':
//        Secondary swirl at destination node (blue colour 0x66ccff), 600ms.
//        Pool: one-shot max-active=2.
//
//   4. MysteryBoxTrappedEvent → phase='tick':
//        Dark-aura ring on caster (1000ms — visible until next round).
//        Pool: SINGLE-INSTANCE per caster (F6 release-first on re-trap).
//        Internal Map<casterId, trappedAuraState> for management.
//
// Pool slots:
//   cast:   one-shot max-active=4
//   impact: one-shot max-active=4
//   active: one-shot max-active=2 (teleport secondary swirl)
//   tick:   single-instance per caster (F6 release-first)
//
// Registry tick contract (HF1):
//   pool.update(_deltaSeconds) IGNORES dt — does NOT call registry.update().
//   The caller (item-vfx-manager at Task 6.1) advances the shared registry once
//   per frame across all pools.

import * as THREE from 'three';
import type {
  GameEvent,
  ItemCastEvent,
  MysteryBoxOutcomeEvent,
  MysteryBoxTeleportEvent,
  MysteryBoxTrappedEvent,
} from '../../../../shared/game/types';
import type { ItemVfxPool, ItemVfxPoolDeps, ItemVfxSpawnArgs } from './item-vfx-types';
import { disposeSkillshotGroup } from './skillshot-shared';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const MAX_CAST_ACTIVE = 4;
const MAX_IMPACT_ACTIVE = 4;
const MAX_TELEPORT_ACTIVE = 2;

/** Cast anticipation lifetime (700ms). */
const CAST_LIFETIME = 0.7;

/** Outcome glyph lifetime (seconds). */
const IMPACT_LIFETIME = 0.6;

/** Teleport secondary-swirl lifetime (seconds). */
const TELEPORT_LIFETIME = 0.6;

/** Trapped aura lifetime (1000ms). */
const TRAPPED_LIFETIME = 1.0;

/** Teleport secondary-swirl colour (blue portal). */
const TELEPORT_COLOUR = 0x66ccff;

/** Trapped aura colour (dark). */
const TRAPPED_COLOUR = 0x220022;

/** Question-mark glyph colour (neutral white-yellow). */
const CAST_COLOUR = 0xffffaa;

/** Outcome colour map. */
const OUTCOME_COLOUR: Record<'blue' | 'gold' | 'dark' | 'gold_fallback_item', number> = {
  blue: 0x66ccff,
  gold: 0xffd24a,
  dark: 0x440044,
  gold_fallback_item: 0xffaa66,
};

// ─────────────────────────────────────────────────────────────────────────────
// Internal state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<ItemVfxPoolDeps['registry']['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Build emitter helpers
// ─────────────────────────────────────────────────────────────────────────────

function buildSpriteEmitter(
  anchor: { x: number; y: number; z: number },
  colour: number,
  lifetime: number,
  label: string,
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  const material = new THREE.SpriteMaterial({
    color: colour,
    transparent: true,
    opacity: 1.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const sprite = new THREE.Sprite(material);
  sprite.position.set(anchor.x, anchor.y, anchor.z);
  sprite.scale.set(0, 0, 1);
  group.add(sprite);

  const handle = registry.create(label);
  state.handle = handle;
  const tl = handle.timeline;

  // Expand 0 → 1.2 over first 40% of lifetime
  tl.to(
    sprite.scale,
    {
      x: 1.2,
      y: 1.2,
      z: 1,
      duration: lifetime * 0.4,
      ease: 'power2.out',
    },
    0,
  );

  // Fade out over remaining 60%
  const opProxy = { value: 1.0 };
  tl.to(
    opProxy,
    {
      value: 0.0,
      duration: lifetime * 0.6,
      ease: 'power1.in',
      onUpdate: () => {
        material.opacity = opProxy.value;
      },
    },
    lifetime * 0.4,
  );

  // Finished marker
  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    lifetime,
  );

  return group;
}

function buildCastEmitter(
  anchor: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  // Question-mark glyph: a spinning sprite
  const material = new THREE.SpriteMaterial({
    color: CAST_COLOUR,
    transparent: true,
    opacity: 0.0,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const sprite = new THREE.Sprite(material);
  sprite.position.set(anchor.x, anchor.y + 0.5, anchor.z);
  sprite.scale.set(0.5, 0.5, 1);
  group.add(sprite);

  const handle = registry.create('mystery_box_cast');
  state.handle = handle;
  const tl = handle.timeline;

  // Fade in quickly (0 → 200ms)
  const opProxy = { value: 0.0 };
  tl.to(
    opProxy,
    {
      value: 1.0,
      duration: CAST_LIFETIME * 0.3,
      ease: 'power1.out',
      onUpdate: () => {
        material.opacity = opProxy.value;
      },
    },
    0,
  );

  // Scale pulse (200ms → 500ms): 0.5 → 1.0 → 0.5
  tl.to(
    sprite.scale,
    {
      x: 1.0,
      y: 1.0,
      z: 1,
      duration: CAST_LIFETIME * 0.5,
      ease: 'power2.inOut',
      yoyo: true,
      repeat: 1,
    },
    CAST_LIFETIME * 0.2,
  );

  // Fade out (500ms → 700ms)
  tl.to(
    opProxy,
    {
      value: 0.0,
      duration: CAST_LIFETIME * 0.3,
      ease: 'power1.in',
      onUpdate: () => {
        material.opacity = opProxy.value;
      },
    },
    CAST_LIFETIME * 0.7,
  );

  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    CAST_LIFETIME,
  );

  return group;
}

function buildTrappedAuraEmitter(
  anchor: { x: number; y: number; z: number },
  deps: ItemVfxPoolDeps,
  state: EmitterState,
): THREE.Group {
  const { registry } = deps;
  const group = new THREE.Group();

  // Dark-aura ring: a ring geometry emitter
  const ringGeometry = new THREE.RingGeometry(0.4, 0.7, 12);
  const ringMaterial = new THREE.MeshBasicMaterial({
    color: TRAPPED_COLOUR,
    transparent: true,
    opacity: 0.0,
    side: THREE.DoubleSide,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
  });
  const ring = new THREE.Mesh(ringGeometry, ringMaterial);
  ring.position.set(anchor.x, anchor.y, anchor.z);
  ring.rotation.x = Math.PI / 2; // lay flat on ground
  group.add(ring);

  const handle = registry.create('mystery_box_trapped_aura');
  state.handle = handle;
  const tl = handle.timeline;

  // Fade in (0 → 300ms)
  const opProxy = { value: 0.0 };
  tl.to(
    opProxy,
    {
      value: 0.8,
      duration: TRAPPED_LIFETIME * 0.3,
      ease: 'power1.out',
      onUpdate: () => {
        ringMaterial.opacity = opProxy.value;
      },
    },
    0,
  );

  // Pulse + hold (300ms → 800ms)
  tl.to(
    opProxy,
    {
      value: 0.4,
      duration: TRAPPED_LIFETIME * 0.5,
      ease: 'power1.inOut',
      onUpdate: () => {
        ringMaterial.opacity = opProxy.value;
      },
    },
    TRAPPED_LIFETIME * 0.3,
  );

  // Fade out (800ms → 1000ms)
  tl.to(
    opProxy,
    {
      value: 0.0,
      duration: TRAPPED_LIFETIME * 0.2,
      ease: 'power1.in',
      onUpdate: () => {
        ringMaterial.opacity = opProxy.value;
      },
    },
    TRAPPED_LIFETIME * 0.8,
  );

  tl.call(
    () => {
      state.finished = true;
    },
    undefined,
    TRAPPED_LIFETIME,
  );

  return group;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory — createMysteryBoxPool
// ─────────────────────────────────────────────────────────────────────────────

export function createMysteryBoxPool(deps: ItemVfxPoolDeps): ItemVfxPool {
  const { scene } = deps;

  /** phase='cast' — one-shot, max-active=4 */
  const castStates = new Map<THREE.Object3D, EmitterState>();

  /** phase='impact' — one-shot, max-active=4 */
  const impactStates = new Map<THREE.Object3D, EmitterState>();

  /** phase='active' — teleport secondary swirl, one-shot, max-active=2 */
  const teleportStates = new Map<THREE.Object3D, EmitterState>();

  /** phase='tick' — trapped aura, single-instance per caster (F6 release-first) */
  const trappedMap = new Map<string, { emitter: THREE.Object3D; state: EmitterState }>();

  let disposed = false;

  // ── acquire ──────────────────────────────────────────────────────────────

  function acquire(args: ItemVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    const { phase } = args;
    const anchor = args.casterPosition ?? args.targetPosition ?? { x: 0, y: 0, z: 0 };

    if (phase === 'cast') {
      if (castStates.size >= MAX_CAST_ACTIVE) return null;
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildCastEmitter(anchor, deps, state);
      scene.add(group);
      castStates.set(group, state);
      return group;
    }

    if (phase === 'impact') {
      if (impactStates.size >= MAX_IMPACT_ACTIVE) return null;
      const outcome = args.outcome ?? 'blue';
      const colour = OUTCOME_COLOUR[outcome];
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildSpriteEmitter(
        anchor,
        colour,
        IMPACT_LIFETIME,
        'mystery_box_outcome',
        deps,
        state,
      );
      scene.add(group);
      impactStates.set(group, state);
      return group;
    }

    if (phase === 'active') {
      if (teleportStates.size >= MAX_TELEPORT_ACTIVE) return null;
      const teleportAnchor = args.targetPosition ?? args.casterPosition ?? { x: 0, y: 0, z: 0 };
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildSpriteEmitter(
        teleportAnchor,
        TELEPORT_COLOUR,
        TELEPORT_LIFETIME,
        'mystery_box_teleport',
        deps,
        state,
      );
      scene.add(group);
      teleportStates.set(group, state);
      return group;
    }

    if (phase === 'tick') {
      // Trapped aura — F6 release-first per casterId
      const casterId = args.casterId ?? '';
      const prior = trappedMap.get(casterId);
      if (prior) {
        prior.state.handle.dispose();
        scene.remove(prior.emitter);
        disposeSkillshotGroup(prior.emitter);
        trappedMap.delete(casterId);
      }
      const state: EmitterState = { handle: null!, finished: false };
      const group = buildTrappedAuraEmitter(anchor, deps, state);
      scene.add(group);
      trappedMap.set(casterId, { emitter: group, state });
      return group;
    }

    return null;
  }

  // ── update ───────────────────────────────────────────────────────────────
  // Registry tick contract (HF1): pool.update does NOT advance the registry.

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    const all = new Set<THREE.Object3D>();
    for (const emitter of castStates.keys()) all.add(emitter);
    for (const emitter of impactStates.keys()) all.add(emitter);
    for (const emitter of teleportStates.keys()) all.add(emitter);
    for (const { emitter } of trappedMap.values()) all.add(emitter);
    return all;
  }

  // ── isFinished ───────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const castState = castStates.get(emitter);
    if (castState) return castState.finished;
    const impactState = impactStates.get(emitter);
    if (impactState) return impactState.finished;
    const teleportState = teleportStates.get(emitter);
    if (teleportState) return teleportState.finished;
    for (const { emitter: te, state } of trappedMap.values()) {
      if (te === emitter) return state.finished;
    }
    // Unknown emitter
    return true;
  }

  // ── release ──────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    // Check cast pool
    const castState = castStates.get(emitter);
    if (castState) {
      castState.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
      castStates.delete(emitter);
      return;
    }

    // Check impact pool
    const impactState = impactStates.get(emitter);
    if (impactState) {
      impactState.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
      impactStates.delete(emitter);
      return;
    }

    // Check teleport pool
    const teleportState = teleportStates.get(emitter);
    if (teleportState) {
      teleportState.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
      teleportStates.delete(emitter);
      return;
    }

    // Check trapped aura map
    for (const [key, { emitter: te, state }] of trappedMap.entries()) {
      if (te === emitter) {
        state.handle.dispose();
        scene.remove(emitter);
        disposeSkillshotGroup(emitter);
        trappedMap.delete(key);
        return;
      }
    }
  }

  // ── dispose (F4) ─────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    for (const [emitter, state] of castStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    castStates.clear();

    for (const [emitter, state] of impactStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    impactStates.clear();

    for (const [emitter, state] of teleportStates) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    teleportStates.clear();

    for (const { emitter, state } of trappedMap.values()) {
      state.handle.dispose();
      scene.remove(emitter);
      disposeSkillshotGroup(emitter);
    }
    trappedMap.clear();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver — resolveMysteryBoxForEvent
// ─────────────────────────────────────────────────────────────────────────────

export function resolveMysteryBoxForEvent(
  event: GameEvent,
  getPositionForPlayer?: (playerId: string) => { x: number; y: number; z: number } | undefined,
  getPositionForNode?: (nodeId: string) => { x: number; y: number; z: number } | undefined,
): { poolId: 'mystery_box'; args: ItemVfxSpawnArgs } | null {
  if (event.type === 'item_cast') {
    const cast = event as ItemCastEvent;
    if (cast.itemId !== 'mystery_box') return null;
    return {
      poolId: 'mystery_box',
      args: {
        phase: 'cast',
        casterId: cast.casterId,
        casterPosition: getPositionForPlayer?.(cast.casterId),
      },
    };
  }

  if (event.type === 'mystery_box_outcome') {
    const outcome = event as MysteryBoxOutcomeEvent;
    return {
      poolId: 'mystery_box',
      args: {
        phase: 'impact',
        casterId: outcome.casterId,
        outcome: outcome.outcome,
        casterPosition: getPositionForPlayer?.(outcome.casterId),
      },
    };
  }

  if (event.type === 'mystery_box_teleport') {
    const teleport = event as MysteryBoxTeleportEvent;
    return {
      poolId: 'mystery_box',
      args: {
        phase: 'active',
        casterId: teleport.casterId,
        targetPosition: getPositionForNode?.(teleport.toNodeId),
      },
    };
  }

  if (event.type === 'mystery_box_trapped') {
    const trapped = event as MysteryBoxTrappedEvent;
    return {
      poolId: 'mystery_box',
      args: {
        phase: 'tick',
        casterId: trapped.casterId,
        casterPosition: getPositionForPlayer?.(trapped.casterId),
      },
    };
  }

  return null;
}
